/**
 * "Analysis" — the self-analysis toggle (fix D / R2 + R4).
 *
 * FAIR PLAY is the whole gate: the engine is offered only when nothing is still
 * being played on this board, so never during a game of mine and never during a
 * live watch. The tile is the ONLY thing that raises `analysisStore.on`, and the
 * rail forces it back down when a new game spawns (`rail/Rail/model.ts`), so
 * there is exactly one way in and two ways out.
 *
 * Fix K: `settled(gate)` alone reads true during the idle tournament wait
 * (§9) too — no game of mine, no live watch, so "nothing is still being
 * played" is technically correct but the wrong question: there is no board
 * to analyse at all. `tournamentIdle` is `rail/surface.ts`'s name for the
 * same guard `stage/EvalBar` / `rail/analysis/EngineHost` read for "is there
 * a real board behind the engine" — reading it here too is what keeps the
 * tile from being the THIRD way `on` could end up true with nothing to show
 * it for.
 */
import {useCallback} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {useLiveGame} from '@/Store/liveGameStore';

import {useAnalysis} from '../../analysis/analysisStore';
import {selectActionGate, settled} from '../gates';
import {showTournamentIdle} from '../../surface';

export type AnalysisActionModel = {
  on: boolean;
  label: string;
  hint: string;
  disabled: boolean;
  onPress: () => void;
};

export function useAnalysisActionModel(): AnalysisActionModel {
  const gate = useLiveGame(useShallow(selectActionGate));
  const tournamentIdle = useLiveGame(showTournamentIdle);
  const on = useAnalysis(s => s.on);
  const setOn = useAnalysis(s => s.setOn);
  const disabled = !settled(gate) || tournamentIdle;

  const onPress = useCallback(() => setOn(!on), [on, setOn]);

  return {
    on,
    label: 'Analysis',
    hint: gate.liveWatch
      ? 'No engine while the game is live'
      : gate.liveGame
      ? 'No engine during your game'
      : tournamentIdle
      ? 'No game to analyse yet'
      : on
      ? 'Stop the engine'
      : 'Analyse this position with Stockfish',
    disabled,
    onPress,
  };
}

export default useAnalysisActionModel;
